import React, { useState, useEffect } from 'react';
import './App.css';
import InstagramInput from './components/InstagramInput';
import PlayLimitMessage from './components/PlayLimitMessage';
import GameScreen from './components/GameScreen';
import Leaderboard from './components/Leaderboard';
import ShareScore from './components/ShareScore';
import PasswordScreen from './components/PasswordScreen';
import CountdownTimer from './components/CountdownTimer';
import WinnerDisplay from './components/WinnerDisplay';
import { api } from './api';
import { canPlayToday, recordPlay, cleanupOldEntries } from './utils/playLimit';

function App() {
  const [gameState, setGameState] = useState('password'); // password, instagramInput, playLimit, preGame, playing, scoreSubmission, leaderboard
  const selectedCity = 'Türkiye'; // Fixed city name
  const selectedCityId = 1; // Fixed city ID
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentScore, setCurrentScore] = useState(0);
  const [instagramUsername, setInstagramUsername] = useState('');
  const [nextPlayTime, setNextPlayTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(null);

  // Cleanup old play limit entries on mount
  // Cleanup old play limit entries on mount
  useEffect(() => {
    cleanupOldEntries();
  }, []);

  // City is now fixed to 'Türkiye' with ID 1

  const handlePasswordCorrect = () => {
    // If we already have Instagram username, skip input step
    if (instagramUsername) {
      // Check daily limit first
      const { canPlay, nextPlayTime: next } = canPlayToday(instagramUsername);
      if (!canPlay) {
        setNextPlayTime(next);
        setGameState('playLimit');
        return;
      }
      // Load leaderboard and proceed
      loadLeaderboardAndProceed(selectedCityId);
    } else {
      setGameState('instagramInput');
    }
  };

  const loadLeaderboardAndProceed = async (cityId) => {
    setLoading(true);
    try {
      const data = await api.getLeaderboard(cityId, instagramUsername);
      setLeaderboardData(data.leaders);
      setUserRank(data.userRank);
      setGameState('preGame');
      setError(null);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Liderlik tablosu yüklenemedi.');
      setLeaderboardData([]);
      setGameState('preGame');
    } finally {
      setLoading(false);
    }
  };

  const handleInstagramSubmit = async (username) => {
    setInstagramUsername(username);
    const { canPlay, nextPlayTime: next } = canPlayToday(username);
    if (!canPlay) {
      setNextPlayTime(next);
      setGameState('playLimit');
      return;
    }
    // Load leaderboard after username accepted
    loadLeaderboardAndProceed(selectedCityId);
  };

  const handleStartGame = async () => {
    setLoading(true);
    try {
      const session = await api.startSession(selectedCityId);
      setCurrentSessionId(session.session_id);
      // Record play for daily limit
      recordPlay(instagramUsername);
      setGameState('playing');
      setError(null);
    } catch (err) {
      console.error('Failed to start game:', err);
      setError('Oyun başlatılamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleGameEnd = (score, duration) => {
    setCurrentScore(score);
    setSessionDuration(duration);
    setGameState('scoreSubmission');
  };

  const handleScoreSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const displayName = `@${instagramUsername}`;
      await api.endSession(currentSessionId, currentScore, selectedCityId, displayName, sessionDuration);
      const data = await api.getLeaderboard(selectedCityId, instagramUsername);
      setLeaderboardData(data.leaders);
      setUserRank(data.userRank);
      setGameState('leaderboard');
      setError(null);
    } catch (err) {
      console.error('Failed to submit score:', err);

      // Check if it's an anti-cheat error
      const errorMessage = err.message || 'Skor kaydedilemedi.';
      if (errorMessage.includes('Invalid score') ||
        errorMessage.includes('Unrealistic') ||
        errorMessage.includes('Too many attempts')) {
        setError(`⚠️ ${errorMessage} Lütfen normal şekilde oynayın.`);
      } else {
        setError('Skor kaydedilemedi. Lütfen tekrar deneyin.');
      }

      try {
        const data = await api.getLeaderboard(selectedCityId, instagramUsername);
        setLeaderboardData(data.leaders);
        setUserRank(data.userRank);
        setGameState('leaderboard');
      } catch (e) {
        setLeaderboardData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const restartGame = () => {
    setGameState('password');
    setLeaderboardData([]);
    setUserRank(null);
    setInstagramUsername('');
    setError(null);
  };

  const goBack = () => {
    setGameState('password');
    setLeaderboardData([]);
    setUserRank(null);
    setInstagramUsername('');
    setError(null);
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🎮 parabulturkiye</h1>
        <CountdownTimer />
      </header>

      <main className="App-main">
        {error && <div className="error-message">❌ {error}</div>}
        {loading && <div className="loading-spinner">⏳ Yükleniyor...</div>}

        {!loading && gameState === 'password' && (
          <>
            <div className="neon-box">
              <WinnerDisplay />
              <div className="instructions">
                <h2>📍 Nasıl Oynanır?</h2>
                <ol>
                  <li>Günün şifresini gir (Story'de!)</li>
                  <li>Instagram kullanıcı adını gir</li>
                  <li>30 saniye boyunca ekrana hızlıca dokun</li>
                  <li>Skorunu kaydet ve kazan!</li>
                </ol>
                <div className="prize-info">
                  <h3>🎁 Ödüller</h3>
                  <p>Her gün en yüksek skor <strong>100 TL</strong> kazanır!</p>
                  <p className="small">Günde 1 kez oynama hakkın var</p>
                </div>
              </div>
            </div>
            <PasswordScreen onPasswordCorrect={handlePasswordCorrect} />
          </>
        )}

        {!loading && gameState === 'instagramInput' && (
          <div className="neon-box">
            <InstagramInput onSubmit={handleInstagramSubmit} onBack={goBack} />
          </div>
        )}

        {!loading && gameState === 'playLimit' && (
          <div className="neon-box">
            <PlayLimitMessage username={instagramUsername} nextPlayTime={nextPlayTime} onBack={goBack} />
          </div>
        )}

        {!loading && gameState === 'preGame' && (
          <div className="neon-box">
            <div className="player-info">
              <p className="player-label">Oynayan</p>
              <p className="player-username">@{instagramUsername}</p>
            </div>
            <Leaderboard city={selectedCity} scores={leaderboardData} userRank={userRank} />
            <div className="button-group">
              <button className="neon-btn primary" onClick={handleStartGame}>
                🎮 Oyunu Başlat
              </button>
              <button className="neon-btn secondary" onClick={goBack}>
                ← Geri Dön
              </button>
            </div>
          </div>
        )}

        {!loading && gameState === 'playing' && (
          <GameScreen onGameEnd={handleGameEnd} />
        )}

        {!loading && gameState === 'scoreSubmission' && (
          <div className="neon-box score-submission">
            <h2>🎉 Oyun Bitti!</h2>
            <div className="final-score">
              <span className="label">Skorun</span>
              <span className="value">{currentScore}</span>
            </div>
            <p className="username-display">@{instagramUsername}</p>
            <form onSubmit={handleScoreSubmit} className="score-submit-form">
              <button type="submit" className="neon-btn primary">
                📊 Skoru Kaydet
              </button>
            </form>
          </div>
        )}

        {!loading && gameState === 'leaderboard' && (
          <div className="neon-box">
            <h2>🏆 Tebrikler!</h2>
            <div className="final-score-display">
              <p>Skorun: <strong>{currentScore}</strong></p>
              <p>@{instagramUsername}</p>
            </div>

            <ShareScore
              score={currentScore}
              username={instagramUsername}
            />

            <Leaderboard
              city={selectedCity}
              scores={leaderboardData}
              userRank={userRank}
            />
            <div className="button-group">
              <button className="neon-btn primary" onClick={restartGame}>
                🔄 Yeniden Oyna (Yarın)
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
